import {useCallback, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import {SidepanelStateChangeAction} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import FlowchartConfirmExitWithUnsavedChangesModal, {
  FlowchartConfirmExitWithUnsavedChangesModalProps,
} from './FlowchartConfirmExitWithUnsavedChangesModal';

const ALL_TABS = 'ALL_TABS';

/**
 * Registered state for a sidepanel tab to track unsaved changes.
 */
export interface SidepanelTabState {
  /** This needs to be unique for each tab, recommend a descriptive name or UUID. */
  tabId: string;
  /** The display name of the tab to display in the confirmation modal if no node is selected */
  tabDisplayName: string;
  isDirty: boolean;
  onDiscard?: () => Promise<void> | void;
}

export type SidepanelRegisterTabState = Omit<SidepanelTabState, 'isDirty'>;

/**
 * Track the active tab for the main sidepanel tabs and the nested tab containers if present.
 */
export interface SidepanelActiveTabState {
  /** The active tab for the main sidepanel tabs */
  activeTabId: string;
  /** The active tab for the nested tab containers if present */
  nestedActiveTabId: string | null;
}

/**
 * Internal state of the sidepanel manager, determines which sidepanel is open, and the node and field that are selected.
 */
export interface SidepanelInternalState {
  nodeId: string | null;
  fieldId: string | null;
  openSidepanelType: SidepanelType | null;
  activeTabState: SidepanelActiveTabState | null;
}

const EMPTY_SIDEPANEL_INTERNAL_STATE: SidepanelInternalState = {
  nodeId: null,
  fieldId: null,
  openSidepanelType: null,
  activeTabState: null,
};

/**
 * Which sidepanel is currently open or being requested to open.
 */
export enum SidepanelType {
  /** Sidepanel responsible for node-level and field-level configuration
   *
   * In Benchling flowcharts, this should be on the right side of the screen.
   */
  NODE_SETTINGS = 'NODE_SETTINGS',
  /** Sidepanel responsible for flowchart-level configuration
   *
   * In Benchling flowcharts, this should be on the left side of the screen.
   */
  FLOWCHART_SETTINGS = 'FLOWCHART_SETTINGS',
}

/**
 * Request to change the sidepanel state by specifying the sidepanel and the node and field to select.
 */
export type SidepanelChange =
  | {
      sidepanel: SidepanelType;
      nodeId: string | null;
      fieldId?: string | null;
    }
  | {sidepanel: null; nodeId?: never; fieldId?: never};

interface UseFlowchartSidepanelManagerReturn {
  registerTab: (params: SidepanelRegisterTabState) => void;
  unregisterTab: (tab: string) => void;
  setIsTabDirty: (tab: string, isDirty: boolean) => void;
  changeTab: (newActiveTabState: SidepanelActiveTabState) => Promise<boolean>;
  changeSidepanel: ({
    sidepanelChange,
    onBeforeSidepanelChanged,
  }: {
    sidepanelChange: SidepanelChange;
    onBeforeSidepanelChanged?: () => Promise<void>;
  }) => Promise<boolean>;
  getEffectiveState: () => SidepanelInternalState;
  forceClose: () => void;
}

export interface UseFlowchartSidepanelManagerOptions {
  /** Function to get the node name by its ID (used for modal display). */
  getNodeNameById: (nodeId: string) => string | null;
  /** The flowchart type name for analytics tracking. */
  flowchartType: string;
}

/**
 * Manages all sidepanel states in the flowchart config editor.
 *
 * Tracks state for both the right and left sidepanels. As each sidepanel opens,
 * its tabs can register to track unsaved changes. Prevents navigating away from
 * tabs when there are unsaved changes and uses a request-style pattern for
 * sidepanel changes. Ensures only one sidepanel can be open at a time.
 *
 * @param options - Configuration options for the sidepanel manager.
 * @returns Object with methods to put into the sidepanel manager context.
 */
export default function useFlowchartSidepanelManager(
  options: UseFlowchartSidepanelManagerOptions
): UseFlowchartSidepanelManagerReturn {
  const {getNodeNameById, flowchartType} = options;
  // Using refs to ensure changes to dirty state don't need a full render cycle before we can check against it
  // when changing the tabs or sidepanel state.
  const tabStates = useRef<Map<string, SidepanelTabState>>(new Map());
  const isModalOpenRef = useRef(false);
  const lastUsedTabByNodeId = useRef<Record<string, SidepanelActiveTabState>>({});
  // Track the effective sidepanel state synchronously to avoid race conditions when multiple state changes happen in quick succession
  const effectiveSidepanelStateRef = useRef<SidepanelInternalState>({
    ...EMPTY_SIDEPANEL_INTERNAL_STATE,
  });

  const [sidepanelState, setSidepanelState] = useState<SidepanelInternalState>({
    ...EMPTY_SIDEPANEL_INTERNAL_STATE,
  });

  /**
   * Updates both the ref and state for the sidepanel
   */
  const updateSidepanelState = useCallback((newState: SidepanelInternalState) => {
    effectiveSidepanelStateRef.current = newState;
    setSidepanelState(newState);
  }, []);

  const getHasDirtyTabs = useCallback(
    () => Array.from(tabStates.current.values()).some((state) => state.isDirty),
    []
  );

  const getHasDirtyState = useCallback(
    (isNestedChange: boolean): boolean => {
      if (isNestedChange) {
        const nestedTabId = sidepanelState.activeTabState?.nestedActiveTabId;
        if (nestedTabId == null) {
          return false;
        }
        const tabState = tabStates.current.get(nestedTabId);
        return tabState?.isDirty ?? false;
      }
      return getHasDirtyTabs();
    },
    [sidepanelState.activeTabState, getHasDirtyTabs]
  );

  const clearAllTabStates = useCallback(() => {
    tabStates.current = new Map();
  }, []);

  const discardTabChanges = useCallback(async (tabId: string | null): Promise<void> => {
    if (tabId != null) {
      const tabState = tabStates.current.get(tabId);
      assert(tabState != null, 'Tab state must exist');
      await tabState.onDiscard?.();
      tabStates.current.delete(tabId);
    }
  }, []);

  const discardAllChanges = useCallback(async (): Promise<void> => {
    await Promise.all(
      Array.from(tabStates.current.values()).map((tabState) => Promise.resolve(tabState.onDiscard?.()))
    );

    clearAllTabStates();
  }, [clearAllTabStates]);

  const registerTab = useCallback((params: SidepanelRegisterTabState) => {
    const {tabId, tabDisplayName, onDiscard} = params;
    const existingState = tabStates.current.get(tabId);

    if (existingState) {
      tabStates.current.set(tabId, {...existingState, tabDisplayName, onDiscard});
    } else {
      tabStates.current.set(tabId, {tabId, tabDisplayName, isDirty: false, onDiscard});
    }
  }, []);

  const unregisterTab = useCallback((tabId: string) => {
    tabStates.current.delete(tabId);
  }, []);

  const setIsTabDirty = useCallback((tabId: string, dirty: boolean) => {
    const currentState = tabStates.current.get(tabId);
    if (currentState && currentState.isDirty !== dirty) {
      tabStates.current.set(tabId, {...currentState, isDirty: dirty});
    }
  }, []);

  const markModalClosed = useCallback(() => {
    isModalOpenRef.current = false;
  }, []);

  const applyIncomingState = useCallback(
    (incomingState: SidepanelInternalState) => {
      updateSidepanelState(incomingState);
      markModalClosed();
    },
    [updateSidepanelState, markModalClosed]
  );

  const handleModalKeepEditing = useCallback(
    (resolve: (value: boolean) => void) => {
      markModalClosed();
      resolve(false);
    },
    [markModalClosed]
  );

  const handleModalLeaveWithoutSaving = useCallback(
    async (
      incomingState: SidepanelInternalState,
      resolve: (value: boolean) => void,
      tabIdToDiscard: string | 'ALL_TABS' | undefined
    ) => {
      if (tabIdToDiscard === ALL_TABS) {
        await discardAllChanges();
      } else if (tabIdToDiscard != null) {
        await discardTabChanges(tabIdToDiscard);
      } else {
        await discardAllChanges();
      }
      applyIncomingState(incomingState);
      resolve(true);
    },
    [discardAllChanges, discardTabChanges, applyIncomingState]
  );

  const getDisplayName = useCallback((): string => {
    if (sidepanelState.nodeId != null) {
      const nodeName = getNodeNameById(sidepanelState.nodeId);
      if (nodeName != null) {
        return nodeName;
      }
    }

    // Get display name from active tab
    const activeTabId = sidepanelState.activeTabState?.activeTabId;
    assert(activeTabId != null, 'Active tab must be set when showing unsaved changes modal');
    const activeTab = tabStates.current.get(activeTabId);
    assert(activeTab?.tabDisplayName != null, 'Active tab must have a display name');
    return activeTab.tabDisplayName;
  }, [sidepanelState.nodeId, sidepanelState.activeTabState, getNodeNameById]);

  const showUnsavedChangesModal = useCallback(
    (incomingState: SidepanelInternalState, isNestedChange: boolean | null = null): Promise<boolean> => {
      if (isModalOpenRef.current) {
        return Promise.resolve(false);
      }

      // Determine which tab to discard based on the change type
      // For nested changes, discard only the nested tab; for top-level or sidepanel changes, discard all
      const tabIdToDiscard =
        isNestedChange === true ? (sidepanelState.activeTabState?.nestedActiveTabId ?? ALL_TABS) : ALL_TABS;

      return new Promise<boolean>((resolve) => {
        isModalOpenRef.current = true;

        legacyRunModal<FlowchartConfirmExitWithUnsavedChangesModalProps>(
          FlowchartConfirmExitWithUnsavedChangesModal,
          {
            nodeName: getDisplayName(),
            // This is a quirk of legacyRunModal, keep editing will be closed on both the button and X clicks
            // But this field and wrapped later on to handle the actual close logic.
            onRequestClose: () => {},
            onRequestKeepEditing: () => {
              handleModalKeepEditing(resolve);
            },
            onRequestLeaveWithoutSaving: () => {
              handleModalLeaveWithoutSaving(incomingState, resolve, tabIdToDiscard);
            },
          }
        );
      });
    },
    [getDisplayName, handleModalKeepEditing, handleModalLeaveWithoutSaving, sidepanelState]
  );

  const applyTabStateChange = useCallback(
    (newActiveTabState: SidepanelActiveTabState): void => {
      const newState = {...sidepanelState, activeTabState: newActiveTabState};
      updateSidepanelState(newState);
    },
    [sidepanelState, updateSidepanelState]
  );

  const checkAndHandleDirtyState = useCallback(
    async (newActiveTabState: SidepanelActiveTabState): Promise<boolean> => {
      const currentState = sidepanelState.activeTabState;
      const isNestedChange =
        currentState != null &&
        currentState.activeTabId === newActiveTabState.activeTabId &&
        currentState.nestedActiveTabId !== newActiveTabState.nestedActiveTabId;

      if (!getHasDirtyState(isNestedChange)) {
        return true;
      }

      const confirmed = await showUnsavedChangesModal(sidepanelState, isNestedChange);
      if (confirmed) {
        applyTabStateChange(newActiveTabState);
        return true;
      }
      return false;
    },
    [showUnsavedChangesModal, sidepanelState, getHasDirtyState, applyTabStateChange]
  );

  /**
   * Determines if within a sidepanel, the user can change to the requested tab state.
   * Updates the active tab state internally if the change is allowed.
   *
   * This should be called in any place where a tab change would be triggered.
   *
   * @param newActiveTabState - The desired active tab state.
   * @returns True if the tab can be changed or is already the same state, false otherwise. If false, caller
   * is responsible for ensuring the requested change is not applied.
   */
  const changeTab = useCallback(
    async (newActiveTabState: SidepanelActiveTabState): Promise<boolean> => {
      // Use the ref to get the most up-to-date state, even if React hasn't re-rendered yet
      const currentState = effectiveSidepanelStateRef.current.activeTabState;

      const areTabStatesEqual =
        currentState != null &&
        currentState.activeTabId === newActiveTabState.activeTabId &&
        currentState.nestedActiveTabId === newActiveTabState.nestedActiveTabId;

      // If no change needed, return early
      if (areTabStatesEqual) {
        return true;
      }

      const canChange = await checkAndHandleDirtyState(newActiveTabState);
      if (!canChange) {
        return false;
      }

      applyTabStateChange(newActiveTabState);

      if (sidepanelState.nodeId != null) {
        lastUsedTabByNodeId.current[sidepanelState.nodeId] = newActiveTabState;
      }

      // Track tab switch analytics
      if (currentState != null) {
        const newTabState = tabStates.current.get(newActiveTabState.activeTabId);
        const tabDisplayName = newTabState?.tabDisplayName ?? newActiveTabState.activeTabId;
        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.SIDEPANEL_VISIBILITY_STATE_CHANGED, {
          actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
          sidepanelAction: SidepanelStateChangeAction.SWITCH_TAB,
          tabDisplayName,
          sidepanelType: sidepanelState.openSidepanelType,
          flowchartType,
        });
      }

      return true;
    },
    [sidepanelState, checkAndHandleDirtyState, applyTabStateChange, flowchartType]
  );

  const forceClose = useCallback(() => {
    // There are situations that supersede discarding unsaved changes
    // (Ex: The selected node or field is being deleted)
    // Just reset the sidepanel state to the empty state
    clearAllTabStates();
    updateSidepanelState(EMPTY_SIDEPANEL_INTERNAL_STATE);
  }, [clearAllTabStates, updateSidepanelState]);

  const buildStateToApply = useCallback(
    (sidepanelChange: SidepanelChange): SidepanelInternalState => {
      const nodeId = sidepanelChange.nodeId ?? null;
      const activeTabState = nodeId != null ? (lastUsedTabByNodeId.current[nodeId] ?? null) : null;

      return {
        nodeId,
        fieldId: sidepanelChange.fieldId ?? null,
        openSidepanelType: sidepanelChange.sidepanel,
        activeTabState,
      };
    },
    [lastUsedTabByNodeId]
  );

  const areSidepanelStatesEqual = useCallback(
    (state1: SidepanelInternalState, state2: SidepanelInternalState): boolean => {
      const isSameNodeId = state1.nodeId === state2.nodeId;
      const isSameFieldId = state1.fieldId === state2.fieldId;
      const isSameOpenSidepanelType = state1.openSidepanelType === state2.openSidepanelType;

      return isSameNodeId && isSameFieldId && isSameOpenSidepanelType;
    },
    []
  );

  const applySidepanelChange = useCallback(
    async ({
      sidepanelChange,
      onBeforeSidepanelChanged,
    }: {
      sidepanelChange: SidepanelChange;
      onBeforeSidepanelChanged?: () => Promise<void>;
    }): Promise<boolean> => {
      let stateToApply = buildStateToApply(sidepanelChange);
      const stayingOnSameNode = sidepanelChange.nodeId === sidepanelState.nodeId;
      const stayingOnSameSidepanel = sidepanelChange.sidepanel === sidepanelState.openSidepanelType;
      const hasDirtyTabs = getHasDirtyTabs();

      if (hasDirtyTabs && (!stayingOnSameNode || !stayingOnSameSidepanel)) {
        const confirmed = await showUnsavedChangesModal(stateToApply);
        if (!confirmed) {
          return false;
        }
      }

      if (onBeforeSidepanelChanged != null) {
        await onBeforeSidepanelChanged();
        stateToApply = buildStateToApply(sidepanelChange);
      }

      if (sidepanelState.nodeId != null && sidepanelState.activeTabState != null && !stayingOnSameNode) {
        lastUsedTabByNodeId.current[sidepanelState.nodeId] = sidepanelState.activeTabState;
      }

      // When switching nodes, use the restored tab state from buildStateToApply (or null if none saved)
      // When staying on same node and sidepanel, preserve the current active tab state
      const isLeavingSidepanel = !(stayingOnSameNode && stayingOnSameSidepanel);
      const newState = {
        ...stateToApply,
        activeTabState: isLeavingSidepanel ? stateToApply.activeTabState : sidepanelState.activeTabState,
      };
      updateSidepanelState(newState);

      return true;
    },
    [getHasDirtyTabs, showUnsavedChangesModal, sidepanelState, buildStateToApply, updateSidepanelState]
  );

  /**
   * Attempts to change the sidepanel to the requested state, and returns true if the change was applied.
   *
   * This should be called in any place where a sidepanel change would be triggered, and the caller should handle any necessary side effects.
   *
   * @param params.sidepanelChange - The sidepanel change request.
   * @param params.onBeforeSidepanelChanged - Optional callback that returns a Promise<void>. If provided, this will be
   * executed after confirming that no modal was needed or the user opted to discard changes. This is useful for
   * applying changes (e.g., adding a new node) and waiting for them to propagate before switching the sidepanel.
   * @returns True if the sidepanel was changed or is already the same state, false otherwise. If false,
   * caller is responsible for ensuring the requested change is not applied.
   */
  const changeSidepanel = useCallback(
    async ({
      sidepanelChange,
      onBeforeSidepanelChanged,
    }: {
      sidepanelChange: SidepanelChange;
      onBeforeSidepanelChanged?: () => Promise<void>;
    }): Promise<boolean> => {
      // Use the ref to get the most up-to-date state, even if React hasn't re-rendered yet
      const currentEffectiveState = effectiveSidepanelStateRef.current;

      const stateToApply = buildStateToApply(sidepanelChange);

      if (areSidepanelStatesEqual(stateToApply, currentEffectiveState)) {
        // Equal sidepanel states is equivalent to a successful sidepanel change.
        // We still want to do the desired prework in this case.
        if (onBeforeSidepanelChanged != null) {
          await onBeforeSidepanelChanged();
        }
        return true;
      }

      return await applySidepanelChange({sidepanelChange, onBeforeSidepanelChanged});
    },
    [buildStateToApply, areSidepanelStatesEqual, applySidepanelChange]
  );

  const getEffectiveState = useCallback((): SidepanelInternalState => sidepanelState, [sidepanelState]);

  return {
    registerTab,
    unregisterTab,
    setIsTabDirty,
    changeTab,
    forceClose,
    getEffectiveState,
    changeSidepanel,
  };
}
